import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { IDrugMilestoneTimelineItem, IDrugApprovalTimelineItem } from '@/components/ai-search/figure/AIMarkdownChart/timeline/types/drug-timeline';
import type { ITwoDimensionAggData, IBullsEyeChartAggOtherInfo } from '@/components/ai-search/figure/AIMarkdownChart/bulls-eye-chart/types/index';
import { combineUrl } from '@eureka/shared/plg-plan/src/utils/url';
import type { BullsEyeChartType } from '@/components/ai-search/figure/AIMarkdownChart/bulls-eye-chart/composables/useBullsEyeCharts';

export interface IPatentTypeView {
    display_name_en?: string
    display_name_cn?: string
}

export interface IPatentDetail {
    patent_id?: string
    pn?: string
    apdt?: number
    exdt?: number
    ti?: string
    pa?: string[]
    simple_legal_status?: string
    extension_type?: string[]
    patent_type_id?: string[]
    patent_type_id_view?: IPatentTypeView[]
    latest_pn?: { pn?: string; patent_id?: string }
    [key: string]: unknown
}

export interface IPatentLayoutResult {
    items: IPatentDetail[]
    total: number
}

export interface GetBullsEyeChartByQueryParams {
    type: BullsEyeChartType
    query?: string
    query_id?: string
}

export interface SynapseChartApi {
    fetchDrugMilestoneTimeline(drugIds: string[], isLogin: boolean): Promise<Record<string, IDrugMilestoneTimelineItem[]>>;
    fetchDrugApprovalTimeline(drugIds: string[], isLogin: boolean): Promise<Record<string, IDrugApprovalTimelineItem[]>>;
    fetchTopDiseaseBullsEyeChart(drugIds: string[], isLogin: boolean): Promise<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>>;
    fetchTopOrgBullsEyeChart(drugIds: string[], isLogin: boolean): Promise<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>>;
    fetchTopDrugTypeBullsEyeChart(drugIds: string[], isLogin: boolean): Promise<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>>;
    fetchTopTargetBullsEyeChart(drugIds: string[], isLogin: boolean): Promise<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>>;
    fetchBullsEyeChartByQuery(data: GetBullsEyeChartByQueryParams, isLogin: boolean): Promise<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>>;
    fetchDrugPatentLayout(drugId: string, isLogin: boolean): Promise<IPatentLayoutResult>;
}

export const SynapseChartApi = Symbol('SynapseChartApi');

@injectable()
export class SynapseChartApiImpl extends PsHttpService implements SynapseChartApi {
    get options() {
        return {
            // 登录用户接口
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/synapse-api/pharmsnap_webapi/1.0`,
            // 游客接口
            baseURLGuest: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/synapse-api/synapse_tourapi/1.0/public`,
            headers: {
                'X-Internal-Request': 'true',
            },
        };
    }

    private getBaseURL(isLogin: boolean): string {
        return isLogin ? this.options.baseURL : this.options.baseURLGuest;
    }

    private getBullsEyeBaseURL(isLogin: boolean): string {
        return isLogin ? combineUrl(this.options.baseURL, '/external') : combineUrl(this.options.baseURL, '/public/external');
    }

    private getPatentLayoutBaseURL(isLogin: boolean): string {
        return isLogin ? combineUrl(this.options.baseURL, '/external') : this.options.baseURLGuest;
    }

    async fetchDrugMilestoneTimeline(drugIds: string[], isLogin: boolean): Promise<Record<string, IDrugMilestoneTimelineItem[]>> {
        return this.post<PatSnapCommonResponseData<Record<string, IDrugMilestoneTimelineItem[]>>>({
            url: `${this.getBaseURL(isLogin)}/drug/milestone_timeline`,
            data: drugIds,
        }).then(res => res as unknown as Record<string, IDrugMilestoneTimelineItem[]>);
    }

    async fetchDrugApprovalTimeline(drugIds: string[], isLogin: boolean): Promise<Record<string, IDrugApprovalTimelineItem[]>> {
        return this.post<PatSnapCommonResponseData<Record<string, IDrugApprovalTimelineItem[]>>>({
            url: `${this.getBaseURL(isLogin)}/drug/approval_timeline`,
            data: drugIds,
        }).then(res => res as unknown as Record<string, IDrugApprovalTimelineItem[]>);
    }

    async fetchTopDiseaseBullsEyeChart(drugIds: string[], isLogin: boolean): Promise<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>> {
        return this.post<PatSnapCommonResponseData<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>>>({
            url: `${this.getBullsEyeBaseURL(isLogin)}/drug/top_disease_bulls_eye_plot`,
            data: drugIds,
        }).then(res => res as unknown as ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>);
    }

    async fetchTopOrgBullsEyeChart(drugIds: string[], isLogin: boolean): Promise<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>> {
        return this.post<PatSnapCommonResponseData<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>>>({
            url: `${this.getBullsEyeBaseURL(isLogin)}/drug/top_org_bulls_eye_plot`,
            data: drugIds,
        }).then(res => res as unknown as ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>);
    }

    async fetchTopDrugTypeBullsEyeChart(drugIds: string[], isLogin: boolean): Promise<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>> {
        return this.post<PatSnapCommonResponseData<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>>>({
            url: `${this.getBullsEyeBaseURL(isLogin)}/drug/top_drug_type_bulls_eye_plot`,
            data: drugIds,
        }).then(res => res as unknown as ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>);
    }

    async fetchTopTargetBullsEyeChart(drugIds: string[], isLogin: boolean): Promise<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>> {
        return this.post<PatSnapCommonResponseData<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>>>({
            url: `${this.getBullsEyeBaseURL(isLogin)}/drug/top_target_bulls_eye_plot`,
            data: drugIds,
        }).then(res => res as unknown as ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>);
    }

    async fetchBullsEyeChartByQuery(data: GetBullsEyeChartByQueryParams, isLogin: boolean): Promise<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>> {
        return this.post<PatSnapCommonResponseData<ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>>>({
            url: `${this.getBullsEyeBaseURL(isLogin)}/drug/bulls_eye_plot`,
            data,
        }).then(res => res as unknown as ITwoDimensionAggData<IBullsEyeChartAggOtherInfo>);
    }

    async fetchDrugPatentLayout(drugId: string, isLogin: boolean): Promise<IPatentLayoutResult> {
        return this.post<PatSnapCommonResponseData<IPatentLayoutResult>>({
            url: `${this.getPatentLayoutBaseURL(isLogin)}/drug/patent/layout`,
            data: { drug_id: drugId },
        }).then(res => res as unknown as IPatentLayoutResult);
    }
}
